/**
 * ImageStatusTile — admin dashboard tile showing image generation
 * coverage across all V1 content packages.
 *
 * Tells the admin at a glance:
 *   - How many packages have a generated infographic
 *   - How many packages have all 8 Pinterest pin images
 *   - How many packages are partial / failed / pending
 */
import { useEffect, useState } from "react";
import {
    Image as ImageIcon,
    CheckCircle2,
    AlertCircle,
    Clock,
    Loader2,
} from "lucide-react";
import { adminImageStatusOverview } from "@/lib/api";

function Stat({ label, value, tone = "muted", testid }) {
    const toneClass = {
        green: "text-[var(--bl-green)]",
        amber: "text-[var(--bl-amber)]",
        rust: "text-[var(--bl-rust)]",
        primary: "text-bl-primary",
        muted: "text-bl-muted",
    }[tone];
    return (
        <div className="space-y-0.5" data-testid={testid}>
            <div className={`text-2xl font-display font-semibold ${toneClass}`}>{value}</div>
            <div className="text-[10px] uppercase tracking-[0.14em] text-bl-muted">{label}</div>
        </div>
    );
}

export default function ImageStatusTile() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const r = await adminImageStatusOverview();
                if (alive) setData(r);
            } catch {
                /* ignore — tile gracefully hides if endpoint flaps */
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, []);

    if (loading) {
        return (
            <div className="bl-card p-4 flex items-center gap-2 text-xs text-bl-muted">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading image coverage…
            </div>
        );
    }
    if (!data) return null;

    const { packages, infographic, pinterest } = data;
    const headlineTone = packages.failed > 0 ? "rust" : packages.partial > 0 ? "amber" : "primary";

    return (
        <div
            className="bl-card p-5 space-y-4"
            data-testid="image-status-tile"
        >
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                    <ImageIcon className="h-4 w-4 text-bl-primary" />
                    <h2 className="font-display font-semibold text-base text-bl tracking-tight">
                        Image generation coverage
                    </h2>
                </div>
                <span className="text-[10px] uppercase tracking-[0.18em] text-bl-muted">
                    V1 Lean Engine · Nano Banana
                </span>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <Stat label="Packages total" value={packages.total} tone="muted" testid="image-stat-total" />
                <Stat
                    label="Complete (9/9)"
                    value={packages.complete}
                    tone="green"
                    testid="image-stat-complete"
                />
                <Stat
                    label="Partial"
                    value={packages.partial}
                    tone={packages.partial > 0 ? "amber" : "muted"}
                    testid="image-stat-partial"
                />
                <Stat
                    label="Failed"
                    value={packages.failed}
                    tone={packages.failed > 0 ? "rust" : "muted"}
                    testid="image-stat-failed"
                />
            </div>

            <div className="border-t border-bl/30 pt-4 grid grid-cols-2 sm:grid-cols-4 gap-4">
                <Stat
                    label="With infographic"
                    value={infographic.with_infographic}
                    tone={infographic.with_infographic === packages.total ? "green" : "primary"}
                    testid="image-stat-info-with"
                />
                <Stat
                    label="Missing infographic"
                    value={infographic.missing_infographic}
                    tone={infographic.missing_infographic > 0 ? "amber" : "muted"}
                    testid="image-stat-info-missing"
                />
                <Stat
                    label="All 8 pins generated"
                    value={pinterest.all_eight_present}
                    tone={pinterest.all_eight_present === packages.total ? "green" : "primary"}
                    testid="image-stat-pins-with"
                />
                <Stat
                    label="Pins missing"
                    value={pinterest.missing_pinterest}
                    tone={pinterest.missing_pinterest > 0 ? "amber" : "muted"}
                    testid="image-stat-pins-missing"
                />
            </div>

            {(packages.failed > 0 || packages.partial > 0) && (
                <div className="text-xs text-bl-muted flex items-start gap-2 pt-1">
                    <AlertCircle className="h-3.5 w-3.5 text-[var(--bl-amber)] mt-0.5 flex-shrink-0" />
                    <span>
                        Open the affected package&rsquo;s <em>Images</em> tab and click
                        Regenerate to fix missing or failed renders.
                    </span>
                </div>
            )}
            {packages.failed === 0 && packages.partial === 0 && packages.total > 0 && (
                <div className="text-xs text-bl-muted flex items-start gap-2 pt-1">
                    <CheckCircle2 className="h-3.5 w-3.5 text-[var(--bl-green)] mt-0.5 flex-shrink-0" />
                    <span>All active packages have a full 9-image asset set.</span>
                </div>
            )}
            {packages.total === 0 && (
                <div className="text-xs text-bl-muted flex items-start gap-2 pt-1">
                    <Clock className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                    <span>No active content packages yet — generate one to see image stats.</span>
                </div>
            )}
        </div>
    );
}
